import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, ArrowRight, Loader2, Mail } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { AssetDownloadButton } from "@/components/marketplace/AssetDownloadButton";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function fetchPreview(previewId) {
  const r = await fetch(`${API}/api/marketplace/cart/preview/${encodeURIComponent(previewId)}`, {
    credentials: "include",
  });
  if (!r.ok) return null;
  return r.json();
}

async function fetchPurchases() {
  const r = await fetch(`${API}/api/marketplace/purchases?limit=200`, {
    credentials: "include",
  });
  if (!r.ok) return null;
  return r.json();
}

export default function CartSuccess() {
  const { preview_id } = useParams();
  const [preview, setPreview] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [prev, pur] = await Promise.all([
        fetchPreview(preview_id),
        fetchPurchases(),
      ]);
      if (cancelled) return;
      setPreview(prev);
      // Filter purchases matching this cart's purchase_id
      const purchaseId = prev?.purchase_id;
      if (purchaseId) {
        const items = (pur?.items || []).filter((t) => t.purchase_id === purchaseId);
        setPurchases(items);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [preview_id]);

  const eur = (c) => (Number(c || 0) / 100).toFixed(2).replace(".", ",");

  return (
    <SidebarLayout>
      <div className="max-w-3xl mx-auto p-6 md:p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="space-y-6" data-testid="cart-success-page">
            <div className="text-center py-8">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[var(--color-success)]/20 border border-[var(--color-success)]/40 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-[var(--color-success)]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">
                Purchase complete!
              </h1>
              <p className="text-sm text-text-secondary">
                {purchases.length > 0
                  ? `${purchases.length} assets ready to download`
                  : "Processing your assets…"}
              </p>
              {preview && (
                <p
                  data-testid="cart-success-total"
                  className="text-xs text-text-secondary mt-2 tabular-nums"
                >
                  Total: ${eur(preview.total_cents)}
                  {preview.discount_pct > 0
                    ? ` · Bundle discount −${preview.discount_pct}%`
                    : ""}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
                <Download className="h-4 w-4 text-[var(--color-secondary)]" /> Downloads
              </h2>
              {purchases.length === 0 ? (
                <p className="text-xs text-text-secondary italic">
                  Nessun asset ancora disponibile. Ricarica la pagina se non vedi i tuoi acquisti.
                </p>
              ) : (
                purchases.map((tx) => (
                  <div
                    key={tx.id}
                    data-testid={`success-tx-${tx.id}`}
                    className="flex items-center justify-between rounded-lg bg-[var(--color-bg)] border border-white/5 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/marketplace/asset/${tx.asset_id}`}
                        className="text-sm font-semibold text-[var(--color-text-main)] hover:text-[var(--color-secondary)] truncate block"
                      >
                        {tx.asset_title || tx.asset_id.slice(0, 12)}
                      </Link>
                      <p className="text-[10px] text-text-secondary tabular-nums">
                        ${eur(tx.gross_cents)} · {tx.creator_type === "virtual" ? "🤖 AI Asset" : "Creator"}
                      </p>
                    </div>
                    <AssetDownloadButton
                      transactionId={tx.id}
                      downloadCount={tx.download_count || 0}
                      maxDownloads={tx.max_downloads || 3}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="rounded-xl bg-[var(--color-panel-ai)] border border-[var(--color-border)] p-4 flex items-start gap-3">
              <Mail className="h-4 w-4 text-[var(--color-secondary)] shrink-0 mt-0.5" />
              <div className="text-xs text-text-secondary">
                A confirmation email with all download links has been sent (sandbox mode).
                You can also access these assets from{" "}
                <Link
                  to="/account/purchases"
                  className="text-[var(--color-secondary)] hover:underline"
                >
                  My purchases
                </Link>{" "}
                anytime.
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to="/marketplace/v10"
                data-testid="cart-success-browse-more-btn"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[var(--color-border)] text-xs uppercase tracking-widest text-text-secondary hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
              >
                Browse more assets <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </SidebarLayout>
  );
}
